import Image from "next/image";
import TableModule from "@components/TableModule";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import Link from "next/link";
import React from "react";
import styled from "styled-components";
import { isEmptyContentfulEntry } from "./contentful";
import {
	desktopLMediaQuery,
	desktopMediaQuery,
	laptopMediaQuery,
	mobileMediaQuery,
	tabletMediaQuery,
} from "./deviceDimensions";
import { fonts } from "./fonts";
import { mixpanelTrack } from "./mixpanel.helper";
import {
	filterUrlPath,
	isAnchorOrDocumentLink,
	isFelixMobileUrl,
} from "./prepareLinks";

const Bold = styled.b`
  font-weight: bold;
`;

const Italic = styled.em`
	font-style: italic;
`;

const Underline = styled.span`
  text-decoration: underline;
`;

const LinkToModule = styled.span`
  text-decoration: underline;
  :hover {
    cursor: pointer;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;

  img {
    width: auto !important;
    height: auto !important;
    max-width: 100% !important;
    object-fit: contain !important;
  }

  ${mobileMediaQuery} {
    margin-top: 20px !important;
    margin-bottom: 5px !important;
  }

  ${tabletMediaQuery} {
    margin-top: 25px !important;
    margin-bottom: 10px !important;
  }

  ${laptopMediaQuery} {
    margin-top: 35px !important;
    margin-bottom: 15px !important;
  }

  ${desktopMediaQuery} {
    margin-top: 40px !important;
    margin-bottom: 15px !important;
  }

  ${desktopLMediaQuery} {
    margin-top: 45px !important;
    margin-bottom: 20px !important;
  }
`;

const SVGImage = styled.img`
  width: auto !important;
  height: auto !important;
  max-width: 100% !important;
  max-height: 300px !important;
  object-fit: contain !important;

  ${mobileMediaQuery} {
    max-height: 200px !important;
  }
`;

const Paragraph = styled.p`
  > div {
    margin: 0px;
    padding: 0px;
  }
  & a {
    font-size: inherit;
  }
`;

const H2 = styled.h2``;
const H4 = styled.h4``;
const H5 = styled.h5``;

const A = styled.a``;
const P = styled.p``;
const OL = styled.ol`
  & li:before {
    font-weight: normal;
    min-width: 20px;
  }
`;
const UL = styled.ul``;
const LI = styled.li``;
const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
`;
const TableBody = styled.tbody``;
const TableRow = styled.tr``;
const TableHeaderCell = styled.th`
	text-align: left;
	vertical-align: top;
`;
const TableCell = styled.td`
	vertical-align: top;
`;

const renderRichTextFunction = (
	content,
	customRenderOptions = {},
	setlightboxContext,
	ariaHidden = false,
) => {
	const isBrowser = () => typeof window !== "undefined";

	const { pathname } = isBrowser() ? new URL(document.URL) : "undefined";

	const renderEntryLink = (node, children) => {
		const target = node.data && node.data.target;

		const nodeType = target.__typename;
		const lightboxHeadline = target?.headline;
		const lightboxMainText = target?.mainText;

		// link to other modules
		switch (nodeType) {
			case "ContentfulModuleLightBox": {
				const onLinkClick = (e) => {
					e.preventDefault();
					setlightboxContext({
						headline: lightboxHeadline,
						mainText: lightboxMainText,
					});
				};

				return (
					<LinkToModule
						data-gtm-cta-position="Hyper link"
						data-gtm-cta-text={children}
						onClick={onLinkClick}
					>
						{children}
					</LinkToModule>
				);
			}
			case "marketingSiteCombined": {
				return <Link href={target.relativeUrl}>{children}</Link>;
			}
			default:
				return null;
		}
	};

	if (content) {
		const { renderMark: customRenderMark, renderNode: customRenderNode = {} } =
			customRenderOptions;

		const renderMark = {
			[MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
			[MARKS.ITALIC]: (text) => <Italic>{text}</Italic>,
			[MARKS.UNDERLINE]: (text) => <Underline>{text}</Underline>,
			...customRenderMark,
		};

		const renderEmbeddedEntryBlock = (node) => {
			const sys = node.data?.target?.sys || {};
			const contentTypeId = sys.contentType?.sys?.id;
			if (!contentTypeId) {
				console.warn("No content type ID found for embedded entry");
				return null;
			}
			if (contentTypeId === "moduleTable") {
				return <TableModule {...node.data.target.fields} />;
			}
			return null;
		};

		const renderEmbeddedAssetBlock = (node) => {
			const target = node.data?.target;
			if (!target) return null;
			const isUnresolvedLink =
				target.sys?.type === "Link" &&
				target.sys.linkType !== undefined &&
				!target.fields;
			if (isUnresolvedLink) return null;

			const fields = target.fields || target;
			const localeTitle =
				typeof fields.title === "object" && fields.title !== null
					? fields.title["en-US"]
					: null;
			const rawTitle = localeTitle ?? fields.title ?? target.title ?? "";
			const alt = typeof rawTitle === "string" ? rawTitle : "";

			const localeFile =
				typeof fields.file === "object" && fields.file !== null
					? (fields.file["en-US"] ?? fields.file)
					: null;
			const rawFile = localeFile ?? fields.file ?? target.file ?? null;
			const file = rawFile?.url ? rawFile : null;

			if (file && file.contentType === "image/svg+xml") {
				const svgSrc = file.url?.startsWith("http")
					? file.url
					: `https:${file.url}`;
				return (
					<ImageContainer>
						<SVGImage src={svgSrc} alt={alt} />
					</ImageContainer>
				);
			}

			if (!file) return null;

			const imgWidth =
				file.details?.image?.width ?? fields.width ?? target.width ?? 833;
			const imgHeight =
				file.details?.image?.height ?? fields.height ?? target.height ?? 416;
			const imgSrc = file.url?.startsWith("http")
				? file.url
				: `https:${file.url}`;
			const safeWidth = imgWidth > 0 ? imgWidth : 833;
			const safeHeight = imgHeight > 0 ? imgHeight : 416;

			return (
				<ImageContainer>
					<Image
						unoptimized
						src={imgSrc}
						alt={alt}
						width={safeWidth}
						height={safeHeight}
					/>
				</ImageContainer>
			);
		};

		/**
		 * Render `list-item` body only. Passing a list-item node as the root to
		 * `documentToReactComponents` re-enters the LIST_ITEM renderer → stack overflow.
		 */
		const listItemBodyAsDocument = (listItemNode) => ({
			nodeType: BLOCKS.DOCUMENT,
			data: {},
			content: Array.isArray(listItemNode?.content) ? listItemNode.content : [],
		});

		/**
		 * LIST_ITEM subtree must recurse with full block renderers. Previously nested
		 * UL/OL LIST_ITEM mapped to bare children → invalid `<ul>` without `<li>` → no bullets.
		 */
		const buildListItemSubtreeOptions = () => ({
			renderMark,
			renderNode: {
				...customRenderNode,
				[BLOCKS.PARAGRAPH]:
					customRenderNode[BLOCKS.PARAGRAPH] ??
					((_node, children1) => <P>{children1}</P>),
				[BLOCKS.UL_LIST]: (_n, c) => <UL>{c}</UL>,
				[BLOCKS.OL_LIST]: (_n, c) => <OL>{c}</OL>,
				[BLOCKS.LIST_ITEM]: (nestedNode) => (
					<LI>
						{documentToReactComponents(
							listItemBodyAsDocument(nestedNode),
							buildListItemSubtreeOptions(),
						)}
					</LI>
				),
				[BLOCKS.HR]: () => <hr />,
				[BLOCKS.HEADING_1]: (_n, c) => <H2>{c}</H2>,
				[BLOCKS.HEADING_2]: (_n, c) => <H2>{c}</H2>,
				[BLOCKS.HEADING_4]: (_n, c) => <H4>{c}</H4>,
				[BLOCKS.HEADING_5]: (_n, c) => <H5>{c}</H5>,
				[BLOCKS.TABLE]: (_n, c) => (
					<Table>
						<TableBody>{c}</TableBody>
					</Table>
				),
				[BLOCKS.TABLE_ROW]: (_n, c) => <TableRow>{c}</TableRow>,
				[BLOCKS.TABLE_HEADER_CELL]: (_n, c) => (
					<TableHeaderCell>{c}</TableHeaderCell>
				),
				[BLOCKS.TABLE_CELL]: (_n, c) => <TableCell>{c}</TableCell>,
				[BLOCKS.EMBEDDED_ASSET]: renderEmbeddedAssetBlock,
				[BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedEntryBlock,
				[INLINES.ENTRY_HYPERLINK]:
					customRenderNode[INLINES.ENTRY_HYPERLINK] ??
					((n, children) => renderEntryLink(n, children)),
				[INLINES.HYPERLINK]:
					customRenderNode[INLINES.HYPERLINK] ??
					((node1, children1) =>
						!isEmptyContentfulEntry(children1) &&
						isAnchorOrDocumentLink(node1.data.uri) ? (
							<A
								onClick={() =>
									mixpanelTrack("text_link_clicked", {
										cta_position: pathname,
										cta_text: children1["0"]["1"],
									})
								}
								href={node1.data.uri}
								tabIndex={ariaHidden ? -1 : undefined}
							>
								{children1}
							</A>
						) : (
							<Link
								onClick={() =>
									mixpanelTrack("text_link_clicked", {
										cta_position: pathname,
										cta_text: children1["0"]["1"],
									})
								}
								href={
									node1.data && isFelixMobileUrl(node1.data.uri)
										? filterUrlPath(node1.data.uri)
										: node1.data.uri
								}
								tabIndex={ariaHidden ? -1 : undefined}
							>
								{children1}
							</Link>
						)),
			},
		});

		const options = {
			renderMark,
			renderNode: {
				[BLOCKS.PARAGRAPH]: (node, children) =>
					!isEmptyContentfulEntry(children) && (
						<Paragraph>{children}</Paragraph>
					),
				[BLOCKS.HEADING_1]: (node, children) => <H2>{children}</H2>,
				[BLOCKS.HEADING_2]: (node, children) => <H2>{children}</H2>,
				[BLOCKS.HEADING_4]: (node, children) => <H4>{children}</H4>,
				[BLOCKS.HEADING_5]: (node, children) => <H5>{children}</H5>,
				[BLOCKS.OL_LIST]: (node, children) => <OL>{children}</OL>,
				[BLOCKS.UL_LIST]: (node, children) => <UL>{children}</UL>,
				[BLOCKS.TABLE]: (_node, children) => (
					<Table>
						<TableBody>{children}</TableBody>
					</Table>
				),
				[BLOCKS.TABLE_ROW]: (_node, children) => (
					<TableRow>{children}</TableRow>
				),
				[BLOCKS.TABLE_HEADER_CELL]: (_node, children) => (
					<TableHeaderCell>{children}</TableHeaderCell>
				),
				[BLOCKS.TABLE_CELL]: (_node, children) => (
					<TableCell>{children}</TableCell>
				),
				[BLOCKS.LIST_ITEM]: (node) => {
					const subtree = documentToReactComponents(
						listItemBodyAsDocument(node),
						buildListItemSubtreeOptions(),
					);
					return <LI>{subtree}</LI>;
				},
				[BLOCKS.HR]: () => <hr />,
				[BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedEntryBlock,
				[BLOCKS.EMBEDDED_ASSET]: renderEmbeddedAssetBlock,

				[INLINES.HYPERLINK]: (node, children) =>
					!isEmptyContentfulEntry(children) &&
					isAnchorOrDocumentLink(node.data.uri) ? (
						<A
							onClick={() =>
								mixpanelTrack("text_link_clicked", {
									cta_position: pathname,
									cta_text: children["0"]["1"],
								})
							}
							target="_blank"
							rel="noopener noreferrer"
							href={node.data.uri}
							tabIndex={ariaHidden ? -1 : undefined}
						>
							{children}
						</A>
					) : (
						<Link
							onClick={() =>
								mixpanelTrack("text_link_clicked", {
									cta_position: pathname,
									cta_text: children["0"]["1"],
								})
							}
							href={
								node.data && isFelixMobileUrl(node.data.uri)
									? filterUrlPath(node.data.uri)
									: node.data.uri
							}
							tabIndex={ariaHidden ? -1 : undefined}
						>
							{children}
						</Link>
					),
				[INLINES.ENTRY_HYPERLINK]: (node, children) =>
					renderEntryLink(node, children),
				...customRenderNode,
			},
			// Preserve line breaks including multiple consecutive ones
			renderText: (text) => {
				// Simply return the text as-is, preserving all newlines
				// The white-space: pre-wrap CSS will handle rendering them
				return text;
			},
		};

		return documentToReactComponents(content, options);
	}
	return `<>`;
};

export default renderRichTextFunction;
